import React, { memo, useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { styled as MUIStyled } from '@mui/system';
import Drawer from '@mui/material/Drawer';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CodeIcon from '@mui/icons-material/Code';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import maritacaLogoPreta from '../../static/images/maritaca_logo_preta.png';
import maritacaLogoBranca from '../../static/images/maritaca_logo_branca.png';
import { usePlayground } from '../../shared/context/PlaygroundContext';
import { useRenderTracker } from '../../utils/perfProbe';
import { PRODUCT_TERMS_AND_POLICY, API_BASE } from '../../shared/Constants';
import { useAuth0 } from "@auth0/auth0-react";
import MoveToProjectSelect from '../projects/MoveToProjectSelect';
import UserSettingsModal from './UserSettingsModal';
import UsageModal, { UsageData } from '../chat/UsageModal';
import { ProjectIcon } from '../projects/projectAppearance';
import { useFeatureFlags } from '../../shared/context/FeatureFlagsContext';
import { useIntegrations } from '../../shared/context/IntegrationsContext';

// Types
type LogoVariant = 'light' | 'dark';
type LogoLoadState = Record<LogoVariant, boolean>;

// Styled Components
const CustomTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '1rem',
  },
});

const ResponsiveDrawer = MUIStyled(Drawer)(({ theme }) => ({
  '@media (min-width: 601px)': {
    display: 'none',
  },
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.background.paper,
  },
}));

const SidebarDesktop = styled.div<{ $collapsed: boolean; $width: number }>`
  height: calc(100vh);
  width: ${({ $width }) => `${$width}px`};
  position: fixed;
  top: 0px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${({ theme }) => theme.sidebar_border};
  background-color: ${({ theme }) => theme.sidebar_background};
  overflow: hidden;
  transition: width 0.18s ease-out;

  @media (max-width: 600px) {
    display: none;
  }
`;

const SidebarHeader = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0 6px 0;
  border-bottom: none;
  transition: padding 0.3s ease;
  position: relative;
  font-family: 'Noka', Lexend;
`;

const NewChatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0 20px;
  transition: background-color 0.12s ease;

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const CollapseButton = styled.button`
  all: unset;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  color: ${({ theme }) => theme.font_color};
  flex-shrink: 0;
  position: relative;

  & svg {
    font-size: 1.3rem;
    transition: transform 0.3s ease;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: -8px -20px;
    pointer-events: none;
  }
`;

const CollapsedBrandLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 0;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  z-index: 1;

  & svg {
    font-size: 2rem;
    color: ${({ theme }) => theme.font_color};
  }
`;

const SidebarLogoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 48px;
`;

const SidebarLogoImage = styled.img<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s ease-out;
  pointer-events: none;
  will-change: opacity;
`;

const SidebarBrandLogo = memo(({ isDark, logosLoaded }: { isDark: boolean; logosLoaded: LogoLoadState }) => {
  const targetVariant: LogoVariant = isDark ? 'dark' : 'light';
  const fallbackVariant: LogoVariant = isDark ? 'light' : 'dark';
  const displayVariant: LogoVariant = logosLoaded[targetVariant]
    ? targetVariant
    : (logosLoaded[fallbackVariant] ? fallbackVariant : targetVariant);

  return (
    <SidebarLogoWrapper role="img" aria-label="Maritaca">
      <SidebarLogoImage
        src={maritacaLogoPreta}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="sync"
        $visible={displayVariant === 'light'}
      />
      <SidebarLogoImage
        src={maritacaLogoBranca}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="sync"
        $visible={displayVariant === 'dark'}
      />
    </SidebarLogoWrapper>
  );
});
SidebarBrandLogo.displayName = 'SidebarBrandLogo';

const BrandLink = styled.a`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  padding-top: 5px;
  padding-bottom: 20px;
  padding-left: 20px;
  text-decoration: none;
  cursor: pointer;
`;

const SidebarChatItemsArea = styled.div`
  display: flex;
  flex-direction: column;
  touch-action: manipulation;
  flex: 1 0;
  overflow-y: auto;
  width: 100%;
  padding: 0;
  padding-bottom: 20px;
  gap: 0.25rem;
  position: relative;
  mask-image: linear-gradient(
    to bottom,
    black calc(100% - 40px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    black calc(100% - 40px),
    transparent 100%
  );

  &::-webkit-scrollbar {
    width: 20px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sidebar_scrollbar};
    border-radius: 6px;
    transition: background-color 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.sidebar_scrollbar_hover};
  }

  &::-webkit-scrollbar-button {
    display: none;
    height: 0;
    width: 0;
  }

  scrollbar-width: auto;
  scrollbar-color: ${({ theme }) => theme.sidebar_scrollbar} transparent;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 20px;
  color: ${({ theme }) => theme.text_secondary};
`;

const SidebarSectionHeader = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  font-family: 'Noka', sans-serif;
  padding: 23px 20px 8px 20px;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &:first-child {
    padding-top: 6px;
  }
`;

const SidebarLowerSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 12px;
  padding-bottom: 16px;
  box-sizing: border-box;
  overflow: hidden;
`;

const SidebarCTAGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

const CTAButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 50px;
  border-radius: 15px;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.font_color};
  background: ${({ theme }) => theme.sidebar_folder_hover_bg};
  transition: background-color .18s ease, box-shadow .18s ease;
  box-sizing: border-box;
  overflow: hidden;

  & span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.9rem;
    font-weight: 400;
    text-align: center;
    color: ${({ theme }) => theme.font_color};
  }

  & svg {
    color: ${({ theme }) => theme.font_color};
    opacity: 0.8;
    font-size: 18px !important;
  }

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
    box-shadow: ${({ theme }) => theme.sidebar_folder_hover_shadow};
    & svg {
      opacity: 1;
    }
  }
`;

const SidebarCreateNewChatButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  padding: 5px 0;
  margin: 0;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  transition: all .12s ease;
  box-sizing: border-box;
  overflow: hidden;

  & span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }

  & svg {
    padding: 0;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.font_color};
    flex-shrink: 0;
    opacity: 0.9;
  }
`;

interface ChatSidebarItemProps {
  isActiveChat?: boolean;
}

const ChatSidebarItem = styled.div<ChatSidebarItemProps>`
  display: flex;
  height: 2.5rem;
  flex-shrink: 0;
  justify-content: flex-start;
  align-items: center;
  padding: 6px 20px 8px 20px;
  column-gap: 6px;
  width: 100%;
  margin: 0;
  border: 1px solid transparent;
  border-radius: 0;
  box-shadow: none;
  color: ${({ theme }) => theme.font_color};
  background-color: ${({ theme, isActiveChat }) => isActiveChat ? theme.sidebar_chat_active_bg : 'transparent'};
  font-size: 0.9rem;
  font-family: 'Sora', 'Lexend';
  cursor: pointer;
  box-sizing: border-box;

  & > span {
    max-width: 90%;
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  & > div { margin-left: auto; }

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const StyledTextField = styled(TextField)`
  width: calc(100% - 10px);
  & .MuiInputBase-root {
    font-size: 0.95rem;
    color: ${({ theme }) => theme.font_color};
    padding: 0;
  }
  & .MuiOutlinedInput-root {
    border-radius: 6px;
    background-color: transparent;
    color: ${({ theme }) => theme.font_color};
    padding: 2px 0;
  }
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  & .MuiInputBase-input {
    &::selection {
      background: ${({ theme }) => theme.selection_background};
      color: ${({ theme }) => theme.selection_color};
    }
    &::-moz-selection {
      background: ${({ theme }) => theme.selection_background};
      color: ${({ theme }) => theme.selection_color};
    }
    padding: 2px 2px 2px 6px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    text-align: left;
  }
  & .MuiInputBase-input::-webkit-input-placeholder {
    color: ${({ theme }) => theme.font_color};
  }
`;

const StyledMoreHorizIcon = styled(MoreHorizIcon)`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.font_color};
`;

const MenuItemPopup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.font_color};

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }

  & svg {
    font-size: 1.2rem;
  }
`;

const CreateChatText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
`;

const InitialLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
`;

const LoadingDotsContainer = styled.div`
  display: flex;
  gap: 6px;
`;

const LoadingDot = styled.div<{ delay: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.font_color};
  opacity: 0.6;
  animation: dotPulse 1.2s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}s;

  @keyframes dotPulse {
    0%, 80%, 100% {
      transform: scale(0.8);
      opacity: 0.4;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const LoadingText = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const MoreChatsLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  gap: 8px;
`;

const MoreChatsLoadingText = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0 20px;
  transition: background-color 0.12s ease;

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const SearchButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  padding: 5px 0;
  margin: 0;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  transition: all .12s ease;
  box-sizing: border-box;
  overflow: hidden;

  & span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    font-size: 0.9rem;
    font-weight: 500;
  }

  & svg {
    padding: 0;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.font_color};
    flex-shrink: 0;
    opacity: 0.9;
  }
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  background-color: ${({ theme }) => theme.input_surface_bg || 'transparent'};
  color: ${({ theme }) => theme.font_color};
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.sidebar_border};
  }
`;

const CloseSearchButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  padding: 4px;
  border-radius: 4px;

  &:hover {
    color: ${({ theme }) => theme.font_color};
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }

  & svg {
    font-size: 1.2rem;
  }
`;

const SidebarDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.sidebar_border};
  margin: 4px 20px;
`;

// Personalidades styled components
const CustomPromptsRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 4px 20px 8px 20px;
`;

const CustomPromptsButton = styled.button<{ $hasActivePrompt?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  border-radius: 15px;
  border: 1px solid ${({ theme }) => theme.font_color};
  background: ${({ theme }) => theme.sidebar_folder_hover_bg};
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  box-sizing: border-box;

  & span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.9rem;
    font-weight: 400;
    text-align: center;
  }

  & svg {
    color: ${({ theme }) => theme.font_color};
  }
`;

const ActiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
`;

// User Profile Section styled components
const UserProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 10px;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const UserAvatar = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const UserName = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Noka', sans-serif;
`;

const UserPlan = styled.span<{ $isPremium?: boolean }>`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${({ theme }) => theme.menu_item_description_color};
  font-family: 'Noka', sans-serif;
`;

const UserMenuModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const UserMenuModal = styled.div<{ $isOpen: boolean }>`
  background: ${({ theme }) => theme.sidebar_background_solid};
  border: 1px solid ${({ theme }) => theme.user_menu_border};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 340px;
  overflow: hidden;
  transform: ${({ $isOpen }) => ($isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: transform 0.2s ease;
`;

const UserMenuHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.user_menu_divider};
`;

const UserMenuHeaderThemeBtn = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 6px;
  border-radius: 8px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.font_color};
  opacity: 0.7;
  transition: background-color 0.15s ease, opacity 0.15s ease;
  &:hover { opacity: 1; background: ${({ theme }) => theme.hover_overlay_light}; }
  svg { font-size: 1.25rem; }
`;

const UserMenuHeaderAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const UserMenuHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const UserMenuHeaderName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Noka', sans-serif;
`;

const UserMenuHeaderEmail = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.menu_item_description_color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Noka', sans-serif;
  margin-top: 2px;
`;

const UserMenuDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.user_menu_divider};
  margin: 4px 0;
`;

const UserMenuItemStyled = styled.div<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  cursor: pointer;
  color: ${({ $danger, theme }) => ($danger ? '#e5484d' : theme.font_color)};
  font-size: 0.9rem;
  font-family: 'Noka', sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }

  svg {
    font-size: 1.2rem;
    flex-shrink: 0;
  }
`;


// Constants
const SIDEBAR_EXPANDED_WIDTH = 280;
const SIDEBAR_EXPANDED_MIN_WIDTH = 220;

// Helper functions
const getGroupLabel = (updatedAtOrTitle?: string): string => {
  if (!updatedAtOrTitle) return 'Semanas anteriores';
  let parsed: Date | null = null;
  const iso = new Date(updatedAtOrTitle);
  if (!isNaN(iso.getTime())) {
    parsed = iso;
  } else {
    const m = updatedAtOrTitle.match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:[\-\s](\d{2}):(\d{2}):(\d{2}))?$/
    );
    if (m) {
      const [, dd, mm, yyyy, HH = '00', MM = '00', SS = '00'] = m;
      parsed = new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(HH),
        Number(MM),
        Number(SS)
      );
    }
  }
  if (!parsed || isNaN(parsed.getTime())) return 'Semanas anteriores';

  const now = new Date();
  const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = toMidnight(now);
  const day = toMidnight(parsed);
  const diffMs = today.getTime() - day.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays <= 7) return 'Essa semana';
  if (diffDays <= 14) return 'Semana passada';
  return 'Semanas anteriores';
};

const GROUP_ORDER = ['Hoje', 'Ontem', 'Essa semana', 'Semana passada', 'Semanas anteriores'];

const buildGroupedItems = (chats: any[]) => {
  const groups: Record<string, any[]> = {
    'Hoje': [],
    'Ontem': [],
    'Essa semana': [],
    'Semana passada': [],
    'Semanas anteriores': []
  };
  chats.forEach((c) => {
    const label = getGroupLabel(c.updated_at || c.title);
    if (!groups[label]) groups[label] = [];
    groups[label].push(c);
  });
  const items: Array<{ type: 'header' | 'chat'; label?: string; chat?: any }> = [];
  GROUP_ORDER.forEach((label) => {
    const arr = groups[label] || [];
    if (arr.length > 0) {
      items.push({ type: 'header', label });
      arr.forEach((chat) => items.push({ type: 'chat', chat }));
    }
  });
  return items;
};

// === Project sidebar styled components (ChatGPT style) ===
const ProjectsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 20px 8px 20px;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  letter-spacing: 0.02em;
  &:hover { color: ${({ theme }) => theme.font_color}; }
  & svg { font-size: 0.9rem; }
`;

const ProjectSidebarItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 14px 8px 14px;
  gap: 6px;
  cursor: default;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.9rem;
  font-family: 'Sora', 'Lexend';
  transition: background-color 0.1s ease;
  &:hover { background-color: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1rem; opacity: 0.5; flex-shrink: 0; }
`;

const ProjectChatSubheader = styled.div`
  padding: 6px 20px 4px 36px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ProjectChatSidebarItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 7px 20px 7px 46px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.9rem;
  font-family: 'Sora', 'Lexend';
  background-color: ${({ theme, $active }) => $active ? theme.sidebar_chat_active_bg : 'transparent'};
  &:hover { background-color: ${({ theme }) => theme.hover_overlay_light}; }
  & > span {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ProjectsSection = styled.div`
  padding: 0;
`;

// Memoized sidebar chat row: per-row booleans + stable handlersRef, so a
// keystroke (which re-renders SideMenu via the shared context) doesn't
// re-render every chat item — each bails unless its own state changed.
const ChatRow = memo((p: any) => {
  const { chat, isActive, showMenu, isEditing, editedTitle, isMobile, openChatMenu, isBgGenerating, isBgCompleted, styledTextFieldRef } = p;
  const h = p.handlersRef;
  return (
    <ChatSidebarItem
                          isActiveChat={isActive}
                          onClick={() => { h.current.handleChatItemClick(chat.chat_id); if (isMobile) h.current.handleSidebarToggle(false); }}
                          onMouseEnter={isMobile ? undefined : () => h.current.setHoveredChatId(chat.chat_id)}
                          onMouseLeave={isMobile ? undefined : () => h.current.setHoveredChatId(null)}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData('text/plain', chat.chat_id); e.dataTransfer.effectAllowed = 'move'; }}
                        >
                          {isEditing ? (
                            <StyledTextField
                              variant="outlined"
                              inputRef={styledTextFieldRef}
                              value={editedTitle}
                              onChange={(e) => h.current.setEditedChatTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') h.current.handleSaveRename(chat.chat_id);
                                if (e.key === 'Escape') {
                                  h.current.setEditingChatId(null);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <>
                              {p.projectMeta && (
                                <span
                                  title={'Projeto: ' + p.projectMeta.name}
                                  style={{ marginRight: 6, fontSize: '0.8rem', flexShrink: 0, opacity: 0.85 }}
                                >
                                  {p.projectMeta.icon}
                                </span>
                              )}
                              <span style={{ flex: 1 }}>{h.current.formatChatTitle(chat.title)}</span>
                              {isBgGenerating && (
                                <CircularProgress size={14} thickness={5} sx={{ ml: 0.5, flexShrink: 0, color: 'text.secondary' }} />
                              )}
                              {isBgCompleted && (
                                <CheckCircleOutlineIcon sx={{ ml: 0.5, flexShrink: 0, fontSize: 16, color: 'success.main' }} />
                              )}
                            </>
                          )}
                          {showMenu && !isEditing && (
                            <IconButton
                              aria-label="mais opções"
                              id={`chat-menu-trigger-${chat.chat_id}`}
                              aria-controls={openChatMenu ? 'ChatMenu' : undefined}
                              aria-expanded={openChatMenu ? 'true' : undefined}
                              aria-haspopup="true"
                              onClick={(e) => h.current.handleOpenChatMenu(e, chat.chat_id, chat.title)}
                              disableRipple
                              sx={{ p: 0 }}
                            >
                              <StyledMoreHorizIcon />
                            </IconButton>
                          )}
                        </ChatSidebarItem>
  );
});
ChatRow.displayName = 'ChatRow';

const SideMenu: React.FC = () => {
  const {
    dark,
    toggleDark,
    isMobile,
    openSidebar,
    effectiveSidebarCollapsed,
    sidebarWidth,
    sidebarLogosLoaded,
    userChats,
    activeChatID,
    loadingInitialChats,
    loadingChatHistory,
    editingChatId,
    editedChatTitle,
    hoveredChatId,
    chatMenuClickedChatId,
    anchorElChatMenu,
    openChatMenu,
    handleSidebarToggle,
    toggleSidebarCollapse,
    handleNewChat,
    setModelChat,
    handleChatItemClick,
    setHoveredChatId,
    setEditingChatId,
    setEditedChatTitle,
    handleRenameConversation,
    handleOpenChatMenu,
    handleCloseChatMenu,
    handleMenuItemClick,
    fetchMoreChats,
    setOpenSubscriptionModal,
    setOpenERPModal,
    setOpenIntegrationsModal,
    formatChatTitle,
    // Search
    searchQuery,
    searchResults,
    isSearching,
    handleSearchChange,
    clearSearch,
    // User info
    userPicture,
    userEmail,
    displayedName,
    planStatus,
    planTitle,
    // User menu actions
    setOpenHelpModal,
    setOpenAccountModal,
    handleLogoutButton,
    handlePlatformButton,
    // Prompt Bank
    setPromptBankOpen,
    // Background generation
    backgroundGeneratingChatIds,
    completedBackgroundChatIds,
  } = usePlayground();
  useRenderTracker('SideMenu', () => ({ userChats, searchResults }));
  const { erp: erpEnabled, scheduled_tasks: scheduledTasksEnabled } = useFeatureFlags();
  const { enabled: integrationsEnabled } = useIntegrations();

  const { getAccessTokenSilently } = useAuth0();
  const styledTextFieldRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isMoveToProjectOpen, setIsMoveToProjectOpen] = useState(false);
  const [moveToProjectChatId, setMoveToProjectChatId] = useState<string>('');

  // Project sidebar state
  const [sidebarProjects, setSidebarProjects] = useState<any[]>([]);
  const [projectsSectionOpen, setProjectsSectionOpen] = useState(false);
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(new Set());
  const [projectChatsMap, setProjectChatsMap] = useState<Record<string, any[]>>({});
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null);

  const openUsage = async () => {
    setIsUserMenuOpen(false);
    setUsageOpen(true);
    setUsageLoading(true);
    setUsageError(null);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/chat/usage`, {
        headers: { authorization: `Auth ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setUsageData(await response.json());
    } catch {
      setUsageData(null);
      setUsageError('Não foi possível carregar seu uso. Tente novamente.');
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchSidebarProjects = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/list`, { headers: { Authorization: `Auth ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSidebarProjects(data.projects || []);
      }
    } catch (e) { /* ignore */ }
  }, [getAccessTokenSilently]);

  const fetchProjectChatsForSidebar = useCallback(async (projectId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/chats`, { headers: { Authorization: `Auth ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setProjectChatsMap(prev => ({ ...prev, [projectId]: data.chats || [] }));
      }
    } catch (e) { /* ignore */ }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchSidebarProjects(); }, [fetchSidebarProjects]);

  // Refresh sidebar projects when projects are created/deleted/updated
  useEffect(() => {
    const handler = () => {
      // Refetch project list, then chats will be fetched by the sidebarProjects useEffect
      fetchSidebarProjects().then(() => {
        // Force refetch chats for all known projects
        Object.keys(projectChatsMap).forEach(pid => fetchProjectChatsForSidebar(pid));
      });
    };
    window.addEventListener("projectsChanged", handler);
    return () => window.removeEventListener("projectsChanged", handler);
  }, [fetchSidebarProjects, fetchProjectChatsForSidebar]);

  // Fetch chats for all projects whenever project list changes
  useEffect(() => {
    if (sidebarProjects.length > 0) {
      sidebarProjects.forEach((p: any) => {
        fetchProjectChatsForSidebar(p.project_id);
      });
    }
  }, [sidebarProjects]);

  // Auto-expand the project containing the active chat
  useEffect(() => {
    if (!activeChatID.chatID) return;
    for (const [pid, chats] of Object.entries(projectChatsMap)) {
      if (chats.some((c: any) => c.chat_id === activeChatID.chatID)) {
        setExpandedProjectIds(prev => {
          if (prev.has(pid)) return prev;
          const next = new Set(prev);
          next.add(pid);
          return next;
        });
        return;
      }
    }
  }, [activeChatID.chatID, projectChatsMap]);

  // chat_id -> project meta (emoji + name), for the badge on sidebar rows.
  const projectMetaByChatId = React.useMemo(() => {
    const meta = new Map<string, { name: string; icon: string }>();
    for (const [pid, chats] of Object.entries(projectChatsMap)) {
      const proj = sidebarProjects.find((sp: any) => sp.project_id === pid);
      (chats as any[]).forEach((c: any) => {
        meta.set(c.chat_id, { name: proj?.name || 'Projeto', icon: proj?.icon || '📁' });
      });
    }
    return meta;
  }, [projectChatsMap, sidebarProjects]);

  // Set of chat IDs that belong to projects (to filter from normal list)
  const projectChatIdsSet = React.useMemo(() => {
    const ids = new Set<string>();
    Object.values(projectChatsMap).forEach((chats: any[]) => {
      chats.forEach((c: any) => ids.add(c.chat_id));
    });
    return ids;
  }, [projectChatsMap]);

  // Auto-expand project when clicking on it
  const handleProjectClick = (projectId: string) => {
    window.dispatchEvent(new CustomEvent('openProjectDetail', { detail: projectId }));
  };

  // Context menu for projects in sidebar
  const [projectMenuAnchor, setProjectMenuAnchor] = useState<HTMLElement | null>(null);
  const [projectMenuId, setProjectMenuId] = useState<string | null>(null);

  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renameProjectValue, setRenameProjectValue] = useState('');

  const handleRenameProjectFromSidebar = async (projectId: string, newName: string) => {
    if (!newName.trim()) { setRenamingProjectId(null); return; }
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/rename`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, name: newName.trim() }),
      });
      fetchSidebarProjects();
      window.dispatchEvent(new CustomEvent('projectsChanged'));
    } catch (e) { /* ignore */ }
    setRenamingProjectId(null);
  };

  const handleDeleteProjectFromSidebar = async (projectId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/delete`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      fetchSidebarProjects();
      window.dispatchEvent(new CustomEvent('projectsChanged'));
    } catch (e) { /* ignore */ }
    setProjectMenuAnchor(null);
    setProjectMenuId(null);
  };

  const handleDropOnProject = async (projectId: string, chatId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST',
        headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: projectId }),
      });
      fetchProjectChatsForSidebar(projectId);
      fetchSidebarProjects();
      // Refresh the main chat list
      setExpandedProjectId(projectId);
    } catch (e) { console.error('Error moving chat to project:', e); }
    setDragOverProjectId(null);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const buffer = 300;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + buffer) {
      fetchMoreChats();
    }
  };

  const handleSaveRename = (chat_id: string) => {
    handleRenameConversation(chat_id, editedChatTitle);
    setEditingChatId(null);
  };

  const chatRowHandlersRef = useRef<any>({});
  chatRowHandlersRef.current = {
    handleChatItemClick, handleSidebarToggle, setHoveredChatId, handleSaveRename,
    setEditingChatId, setEditedChatTitle, handleOpenChatMenu, formatChatTitle,
  };

  const sidebarContent = () => (
    <>
      <SidebarHeader $collapsed={effectiveSidebarCollapsed}>
        {!effectiveSidebarCollapsed && (
          <>
            <BrandLink
              href="https://maritaca.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              maritaca.ai
            </BrandLink>
            <NewChatRow>
              <SidebarCreateNewChatButton onClick={() => { handleNewChat(); }}>
                <ControlPointIcon style={{ fontSize: "1.4rem" }} />
                <CreateChatText>Novo chat</CreateChatText>
              </SidebarCreateNewChatButton>
              {!isMobile ? (
                <CollapseButton onClick={(e) => { e.stopPropagation(); toggleSidebarCollapse(); }}>
                  <KeyboardTabIcon style={{ transform: 'rotate(180deg)' }} />
                </CollapseButton>
              ) : (
                <CollapseButton onClick={(e) => { e.stopPropagation(); handleSidebarToggle(false); }}>
                  <KeyboardTabIcon style={{ transform: 'rotate(180deg)' }} />
                </CollapseButton>
              )}
            </NewChatRow>
            {!isSearchExpanded ? (
              <SearchRow>
                <SearchButton onClick={() => { setIsSearchExpanded(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}>
                  <SearchIcon style={{ fontSize: "1.4rem" }} />
                  <span>Buscar conversas</span>
                </SearchButton>
              </SearchRow>
            ) : (
              <SearchInputContainer>
                <SearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar conversas..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      clearSearch();
                      setIsSearchExpanded(false);
                    }
                  }}
                  autoFocus
                />
                <CloseSearchButton onClick={() => { clearSearch(); setIsSearchExpanded(false); }}>
                  <CloseIcon />
                </CloseSearchButton>
              </SearchInputContainer>
            )}
            <SearchRow>
              <SearchButton onClick={() => setPromptBankOpen(true)}>
                <CategoryIcon style={{ fontSize: "1.4rem" }} />
                <span>Biblioteca de prompts</span>
              </SearchButton>
            </SearchRow>
            <SearchRow>
              <SearchButton onClick={() => window.dispatchEvent(new CustomEvent("openProjectsView"))}>
                <FolderIcon style={{ fontSize: "1.4rem" }} />
                <span>Projetos</span>
              </SearchButton>
            </SearchRow>
            {scheduledTasksEnabled && (
              <SearchRow>
                <SearchButton onClick={() => window.dispatchEvent(new CustomEvent("openTasksView"))}>
                  <ScheduleIcon style={{ fontSize: "1.4rem" }} />
                  <span>Tarefas agendadas</span>
                </SearchButton>
              </SearchRow>
            )}
            <SidebarDivider />
          </>
        )}
        {effectiveSidebarCollapsed && (
          <>
            <CollapsedBrandLink
              href="https://maritaca.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SidebarBrandLogo isDark={dark} logosLoaded={sidebarLogosLoaded} />
            </CollapsedBrandLink>
            <CollapseButton onClick={toggleSidebarCollapse} style={{ width: '100%' }}>
              <MenuIcon />
            </CollapseButton>
          </>
        )}
      </SidebarHeader>

      {!effectiveSidebarCollapsed && (
        <>
          <SidebarChatItemsArea onScroll={handleScroll}>
            {loadingInitialChats ? (
              <InitialLoadingContainer>
                <LoadingDotsContainer>
                  <LoadingDot delay={0} />
                  <LoadingDot delay={0.2} />
                  <LoadingDot delay={0.4} />
                </LoadingDotsContainer>
                <LoadingText>Carregando conversas</LoadingText>
              </InitialLoadingContainer>
            ) : (
              <>
                {isSearching && (
                  <InitialLoadingContainer>
                    <LoadingDotsContainer>
                      <LoadingDot delay={0} />
                      <LoadingDot delay={0.2} />
                      <LoadingDot delay={0.4} />
                    </LoadingDotsContainer>
                    <LoadingText>Buscando conversas</LoadingText>
                  </InitialLoadingContainer>
                )}
{/* Projetos é um item de navegação no cabeçalho da sidebar */}

                {!isSearching && (() => {
                  // Use search results if available, otherwise use all chats.
                  // Project conversations are INCLUDED (they used to be filtered
                  // out for the old inline project folders; when Projetos became
                  // a single nav item they turned invisible here). Each carries
                  // the project's emoji + name as a small badge for context.
                  const chats = searchResults !== null
                    ? (Array.isArray(searchResults) ? searchResults : [])
                    : (Array.isArray(userChats) ? userChats : []);
                  const items = buildGroupedItems(chats);
                  let chatIndex = -1;
                  return items.map((item, idx) => {
                    if (item.type === 'header') {
                      return (
                        <SidebarSectionHeader key={`hdr-${item.label}`}>{item.label}</SidebarSectionHeader>
                      );
                    } else {
                      const chat = item.chat!;
                      chatIndex += 1;
                      let isEditingChat = editingChatId === chat.chat_id;
                      const isActiveChat = chat.chat_id === activeChatID.chatID;
                      const isHovered = hoveredChatId === chat.chat_id;
                      const isMenuOpen = chatMenuClickedChatId === chat.chat_id;
                      // On touch/mobile the "..." is always shown. Revealing it on
                      // hover made the first tap register as a hover (iOS), so it took
                      // two taps to actually open the conversation. Always-visible →
                      // the first tap goes straight to onClick.
                      const showMenu = isHovered || isMenuOpen || isEditingChat || isMobile;

                      return (
                        <ChatRow
                          key={chat.chat_id || `chat-${chatIndex}`}
                          chat={chat}
                          isActive={isActiveChat}
                          showMenu={showMenu}
                          isEditing={isEditingChat}
                          editedTitle={isEditingChat ? editedChatTitle : ''}
                          isMobile={isMobile}
                          openChatMenu={openChatMenu}
                          isBgGenerating={backgroundGeneratingChatIds.has(chat.chat_id)}
                          isBgCompleted={completedBackgroundChatIds.has(chat.chat_id)}
                          styledTextFieldRef={styledTextFieldRef}
                          handlersRef={chatRowHandlersRef}
                          projectMeta={projectMetaByChatId.get(chat.chat_id) || null}
                        />
                      );
                    }
                  });
                })()}
                {searchResults !== null && searchResults.length === 0 && !isSearching && (
                  <EmptyState>
                    <span>Nenhum resultado encontrado</span>
                  </EmptyState>
                )}
                {searchResults === null && (!userChats || userChats.length === 0) && (
                  <EmptyState>
                    <span>Nenhuma conversa encontrada</span>
                  </EmptyState>
                )}
                {loadingChatHistory && searchResults === null && (
                  <MoreChatsLoadingContainer>
                    <CircularProgress size={16} />
                    <MoreChatsLoadingText>Carregando mais conversas</MoreChatsLoadingText>
                  </MoreChatsLoadingContainer>
                )}
              </>
            )}
          </SidebarChatItemsArea>

          <Menu
            id="ChatMenu"
            anchorEl={anchorElChatMenu}
            open={openChatMenu}
            onClose={(_event, _reason) => handleCloseChatMenu()}
            PaperProps={{
              sx: (theme) => ({
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
                border: 'none',
                borderRadius: '12px',
                bgcolor: theme.palette.mode === 'dark' ? '#232321' : 'white',
                color: theme.palette.mode === 'dark' ? 'white' : '#232321',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 24px rgba(0,0,0,0.4)'
                  : '0 8px 24px rgba(0,0,0,0.12)',
                minWidth: '200px',
              })
            }}
            MenuListProps={{
              'aria-labelledby': anchorElChatMenu ? anchorElChatMenu.id : undefined,
              sx: { padding: '8px' }
            }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            TransitionComponent={Collapse}
          >
            <MenuItemPopup onClick={(e) => handleMenuItemClick(e as any, 'rename')}>
              <ModeEditOutlineIcon /> <span>Renomear</span>
            </MenuItemPopup>
            <MenuItemPopup onClick={(e) => handleMenuItemClick(e as any, 'delete')}>
              <DeleteOutlineIcon sx={{ color: '#991b1b' }} /> <span style={{ color: '#991b1b' }}>Apagar</span>
            </MenuItemPopup>
            <MenuItemPopup onClick={() => {
              if (chatMenuClickedChatId) {
                setMoveToProjectChatId(chatMenuClickedChatId);
                setIsMoveToProjectOpen(true);
              }
              handleCloseChatMenu();
            }}>
              <DriveFileMoveIcon /> <span>Mover para projeto</span>
            </MenuItemPopup>
          </Menu>

          <SidebarLowerSection>
            <UserProfileSection onClick={() => setIsUserMenuOpen(true)}>
              <UserAvatar src={userPicture} alt="Avatar" />
              <UserInfo>
                <UserName>{displayedName || userEmail}</UserName>
                <UserPlan data-testid="plan-badge" $isPremium={planStatus === 'active'}>
                  {planStatus === 'active' ? (planTitle || 'Premium') : planStatus === 'free' ? 'Gratuito' : ' '}
                </UserPlan>
              </UserInfo>
            </UserProfileSection>
          </SidebarLowerSection>
        </>
      )}

      {effectiveSidebarCollapsed && (
        <>
          <SidebarChatItemsArea style={{ alignItems: 'center', padding: '10px 5px', gap: '10px' }}>
            <CustomTooltip title="Nova conversa" placement="right">
              <IconButton
                onClick={() => { handleNewChat(); }}
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                  }
                }}
              >
                <ControlPointIcon />
              </IconButton>
            </CustomTooltip>
          </SidebarChatItemsArea>
          <SidebarLowerSection
            style={{
              padding: '12px 6px 8px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CustomTooltip title={displayedName || userEmail || 'Menu'} placement="right">
              <UserAvatar
                src={userPicture}
                alt="Avatar"
                onClick={() => setIsUserMenuOpen(true)}
                style={{ width: 36, height: 36, cursor: 'pointer' }}
              />
            </CustomTooltip>
          </SidebarLowerSection>
        </>
      )}
    </>
  );

  return (
    <>
      <SidebarDesktop $collapsed={effectiveSidebarCollapsed} $width={sidebarWidth}>
        {sidebarContent()}
      </SidebarDesktop>

      <ResponsiveDrawer
        anchor="left"
        open={openSidebar}
        onClose={() => handleSidebarToggle(false)}
        PaperProps={{
          sx: (theme) => ({
            width: sidebarWidth,
            maxWidth: SIDEBAR_EXPANDED_WIDTH,
            minWidth: SIDEBAR_EXPANDED_MIN_WIDTH,
            backgroundColor: theme.palette.background.paper,
            transition: 'width 0.18s ease-out',
          })
        }}
      >
        {sidebarContent()}
      </ResponsiveDrawer>

      {/* User Menu Modal */}
      <UserMenuModalOverlay $isOpen={isUserMenuOpen} onClick={() => setIsUserMenuOpen(false)}>
        <UserMenuModal $isOpen={isUserMenuOpen} onClick={(e) => e.stopPropagation()}>
          <UserMenuHeader>
            <UserMenuHeaderAvatar src={userPicture} alt="Avatar" />
            <UserMenuHeaderInfo>
              <UserMenuHeaderName>{displayedName || userEmail}</UserMenuHeaderName>
              {displayedName && userEmail && (
                <UserMenuHeaderEmail>{userEmail}</UserMenuHeaderEmail>
              )}
            </UserMenuHeaderInfo>
            <UserMenuHeaderThemeBtn onClick={() => toggleDark()} title={dark ? 'Modo claro' : 'Modo escuro'} aria-label="Alternar tema">
              {dark ? <LightModeIcon /> : <DarkModeIcon />}
            </UserMenuHeaderThemeBtn>
          </UserMenuHeader>

          <UserMenuItemStyled onClick={() => { setIsUserSettingsOpen(true); setIsUserMenuOpen(false); }}>
            <SettingsOutlinedIcon />
            <span>Personalização</span>
          </UserMenuItemStyled>
          <UserMenuItemStyled role="button" aria-label="Uso" tabIndex={0} onClick={openUsage}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') openUsage(); }}>
            <AssessmentOutlinedIcon />
            <span>Uso</span>
          </UserMenuItemStyled>
          <UserMenuItemStyled onClick={() => { setOpenSubscriptionModal(true); setIsUserMenuOpen(false); }}>
            <ArrowOutwardIcon />
            <span>Meu plano</span>
          </UserMenuItemStyled>

          <UserMenuDivider />

          <UserMenuItemStyled onClick={() => { handlePlatformButton(); setIsUserMenuOpen(false); }}>
            <CodeIcon />
            <span>Use via API</span>
          </UserMenuItemStyled>
          {erpEnabled && (
            <UserMenuItemStyled onClick={() => { setOpenERPModal(true); setIsUserMenuOpen(false); }}>
              <IntegrationInstructionsIcon />
              <span>Integrações ERP</span>
            </UserMenuItemStyled>
          )}
          {integrationsEnabled && (
            <UserMenuItemStyled onClick={() => { setOpenIntegrationsModal(true); setIsUserMenuOpen(false); }}>
              <HubOutlinedIcon />
              <span>Conectores</span>
            </UserMenuItemStyled>
          )}
          <UserMenuItemStyled onClick={() => { setOpenHelpModal(true); setIsUserMenuOpen(false); }}>
            <HelpOutlineIcon />
            <span>Ajuda</span>
          </UserMenuItemStyled>
          <UserMenuItemStyled onClick={() => { window.open(PRODUCT_TERMS_AND_POLICY, "_blank"); setIsUserMenuOpen(false); }}>
            <FormatListBulletedIcon />
            <span>Termos e condições</span>
          </UserMenuItemStyled>

          <UserMenuDivider />

          <UserMenuItemStyled onClick={() => { setOpenAccountModal(true); setIsUserMenuOpen(false); }}>
            <ManageAccountsOutlinedIcon />
            <span>Conta</span>
          </UserMenuItemStyled>
          <UserMenuItemStyled onClick={() => { handleLogoutButton(); setIsUserMenuOpen(false); }}>
            <LogoutIcon />
            <span>Sair</span>
          </UserMenuItemStyled>
        </UserMenuModal>
      </UserMenuModalOverlay>

      <UsageModal open={usageOpen} onClose={() => setUsageOpen(false)} loading={usageLoading} error={usageError} usage={usageData} />

      <UserSettingsModal open={isUserSettingsOpen} onClose={() => setIsUserSettingsOpen(false)} onBack={() => { setIsUserSettingsOpen(false); setIsUserMenuOpen(true); }} />

      {/* Move to Project Select */}
      <MoveToProjectSelect
        open={isMoveToProjectOpen}
        onClose={() => { setIsMoveToProjectOpen(false); setMoveToProjectChatId(''); }}
        chatId={moveToProjectChatId}
        onMoved={() => { setIsMoveToProjectOpen(false); setMoveToProjectChatId(''); }}
      />
    </>
  );
};

export default SideMenu;
